import { type HTMLAttributes, type ReactNode } from "react";
import { messages } from "@/shared/i18n";
import { AlertCircle, CheckCircle, AlertTriangle, Info, X } from "@/shared/components/icons";
import { cn } from "@/shared/lib/cn";

type AlertType = "error" | "success" | "warning" | "info";

interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "children" | "title"> {
  type: AlertType;
  message?: string | null;
  title?: string;
  children?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const alertStyles: Record<AlertType, string> = {
  error: "bg-red-50 border-red-200 text-red-700",
  success: "bg-green-50 border-green-200 text-green-700",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-700",
  info: "bg-blue-50 border-blue-200 text-blue-700",
};

const iconColors: Record<AlertType, string> = {
  error: "text-red-500",
  success: "text-green-500",
  warning: "text-yellow-500",
  info: "text-blue-500",
};

const iconComponents: Record<AlertType, typeof AlertCircle> = {
  error: AlertCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
};

function AlertIcon({ type }: { type: AlertType }) {
  const Icon = iconComponents[type];
  return <Icon className={cn("h-5 w-5 shrink-0", iconColors[type])} />;
}

export function Alert({
  type,
  message,
  title,
  children,
  dismissible = false,
  onDismiss,
  className,
  ...rest
}: AlertProps) {
  const content = message ?? children;

  if (!content && !title) {
    return null;
  }

  return (
    <div
      {...rest}
      className={cn("flex items-start gap-3 rounded-lg border p-4", alertStyles[type], className)}
      role={type === "error" ? "alert" : "status"}
    >
      <AlertIcon type={type} />
      <div className="min-w-0 flex-1">
        {title && <p className="mb-1 font-medium">{title}</p>}
        {typeof content === "string" ? (
          <p className="text-sm">{content}</p>
        ) : (
          <div className="text-sm">{content}</div>
        )}
      </div>
      {dismissible && onDismiss && (
        <button
          aria-label={messages.components.accessibility.dismissAlert}
          className="ml-auto shrink-0 rounded p-1 opacity-70 transition-opacity hover:opacity-100"
          type="button"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
